import { ReactNode, useState } from 'react';
import { Info } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  SectionLayout,
  SectionLayoutSkeleton,
} from '@/components/ui/SectionLayout.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import useGetEmailSenderSettings from '@/features/dashboard/emailSender/hooks/useGetEmailSenderSettings.ts';
import EditEmailSenderModal from '@/features/dashboard/emailSender/components/EditEmailSenderModal.tsx';
import { EmailSettingStatus } from '@/features/dashboard/emailSender/types/EmailSettingStatus.ts';
import {
  emailSettingStatusTones,
  getEmailSettingStatusLabel,
} from '@/features/dashboard/emailSender/types/EmailSettingStatus.utils.ts';

const DefaultValue = ({ children }: { children: ReactNode }) => (
  <span className="font-normal text-neutral-500">{children}</span>
);

/**
 * Domena w trakcie zatwierdzania to jedyny stan, który wymaga od firmy
 * cierpliwości, a nie działania — stoi pod wierszami jako notka, nie jako
 * kolejna wartość.
 */
const ApprovalNotice = () => (
  <div className="flex items-start gap-2.5 rounded-[10px] border border-accent-border bg-accent-100 p-3">
    <Info aria-hidden className="mt-px size-4 shrink-0 text-accent-800" />
    <div className="min-w-0">
      <p className="text-[13px] font-bold text-accent-800">
        Zatwierdzamy Twoją domenę
      </p>
      <p className="mt-0.5 text-[13px] font-normal leading-[1.5] text-neutral-700">
        Skontaktujemy się w sprawie konfiguracji DNS. Do czasu zatwierdzenia
        wiadomości wychodzą z domeny Campdesk — uczestnicy zobaczą adres firmy w
        polu Reply-To.
      </p>
    </div>
  </div>
);

const EmailSenderSection = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { data, isLoading } = useGetEmailSenderSettings();

  if (isLoading || !data) {
    return <SectionLayoutSkeleton rows={3} />;
  }

  const hasAnySetting =
    data.fromEmail !== null ||
    data.fromName !== null ||
    data.replyToEmail !== null;

  const isAwaitingApproval =
    data.fromEmailStatus === EmailSettingStatus.REQUESTED ||
    data.replyToStatus === EmailSettingStatus.REQUESTED;

  return (
    <>
      <SectionLayout
        title="Nadawca e-mail"
        description="Adres i nazwa, z których wychodzą wiadomości do uczestników. Własny adres i Reply-To wymagają zatwierdzenia przez Campdesk."
      >
        {hasAnySetting ? (
          <div className="space-y-3">
            <InfoCardList>
              <InfoCardRow
                layout="columns"
                label="Adres nadawcy"
                action={
                  <ActionButton
                    tone="outline"
                    size="toolbar"
                    onClick={() => setIsEditModalOpen(true)}
                  >
                    Zmień
                  </ActionButton>
                }
              >
                {/*
                 * Na wąskim ekranie adres i „Zmień” zostają w jednej linii,
                 * a stan schodzi niżej — zawijanie siedzi w slocie wartości.
                 */}
                <div className="flex flex-wrap items-center gap-2">
                  {data.fromEmail ? (
                    <>
                      <span>{data.fromEmail}</span>
                      {data.fromEmailStatus && (
                        <StatusDot
                          tone={emailSettingStatusTones[data.fromEmailStatus]}
                          label={getEmailSettingStatusLabel(
                            data.fromEmailStatus,
                          )}
                        />
                      )}
                    </>
                  ) : (
                    <DefaultValue>Domena Campdesk (domyślnie)</DefaultValue>
                  )}
                </div>
              </InfoCardRow>
              <InfoCardRow layout="columns" label="Nazwa nadawcy">
                {data.fromName ?? (
                  <DefaultValue>Nazwa firmy (domyślnie)</DefaultValue>
                )}
              </InfoCardRow>
              <InfoCardRow layout="columns" label="Reply-To">
                <div className="flex flex-wrap items-center gap-2">
                  {data.replyToEmail ? (
                    <>
                      <span>{data.replyToEmail}</span>
                      {/*
                       * Projekt rysuje ten wiersz bez stanu, bo w makiecie
                       * Reply-To jest domyślny. Odrzucony albo zawieszony adres
                       * musi być jednak widoczny — bez tego firma nie wie,
                       * dlaczego odpowiedzi nie dochodzą.
                       */}
                      {data.replyToStatus && (
                        <StatusDot
                          tone={emailSettingStatusTones[data.replyToStatus]}
                          label={getEmailSettingStatusLabel(data.replyToStatus)}
                        />
                      )}
                    </>
                  ) : (
                    <DefaultValue>Adres nadawcy (domyślnie)</DefaultValue>
                  )}
                </div>
              </InfoCardRow>
            </InfoCardList>
            {isAwaitingApproval && <ApprovalNotice />}
          </div>
        ) : (
          <NoItems
            title="Wiadomości wychodzą z domeny Campdesk"
            description="Dodaj własny adres nadawcy, aby uczestnicy widzieli Twoją firmę jako wysyłającego."
          >
            <ActionButton
              tone="outline"
              size="toolbar"
              onClick={() => setIsEditModalOpen(true)}
            >
              Ustaw nadawcę
            </ActionButton>
          </NoItems>
        )}
      </SectionLayout>
      {isEditModalOpen && (
        <EditEmailSenderModal
          currentSettings={data}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}
    </>
  );
};

export default EmailSenderSection;
